/**
 * _factory.ts — Factory para generar productos demo completamente poblados.
 *
 * QUÉ HACE:
 *   Expone `buildProduct(input)` que normaliza un producto demo con TODOS los
 *   campos del tipo Producto listos para Inventario, Caja y Tienda:
 *     - Identificación (id, código, SKU)
 *     - Precios (precio, costo, mayorista, VIP, descuentoMáx, precioProveedor)
 *     - Inventario (stock, ubicación, proveedor, fechas, vencimiento)
 *     - Tienda (descripción larga, marca, modelo, origen, especificaciones…)
 *     - Avanzado (gastoFactors con 10 gastos operativos activos)
 *     - Receta (si es producto combinado)
 *
 * QUÉ EXPORTA: buildProduct, type ProductInput, helpers (img, img3)
 *
 * QUÉ NO DEBE HACER:
 *   - NO debe importar React ni dependencias de UI
 *   - NO debe leer de disco ni hacer fetch
 *
 * REGLAS:
 *   - Los IDs siguen el patrón ${categoryId}_${index} (ej: cat_beb_0)
 *   - El código SKU sigue ${skuPrefix}-${index+1:03} (ej: BEB-001)
 *   - Todos los precios se redondean a múltiplos de 50 para parecer reales
 *   - Las imágenes usan source.unsplash.com con keywords (compatible con galería de 3)
 */

import { daysAgo } from "../../utils";
import { inferVariantesPorCategoria } from "./_variants";
import type {
  Producto,
  VariantGroup,
  VariantSKU,
  ModifierGroup,
  DisponibilidadHoraria,
} from "@/types";

// ───────────────────────── Utilidades numéricas ────────────────────────────
const roundTo = (value: number, step: number): number =>
  Math.round(value / step) * step;

const pickStep = (value: number): number => {
  if (value >= 100000) return 1000;
  if (value >= 10000) return 500;
  if (value >= 1000) return 100;
  return 50;
};

/** Redondea a número "bonito" (ej: 15.000, 9.900). */
const smartRound = (value: number): number => roundTo(value, pickStep(value));

/** Random determinista a partir de un string (para fechas/velocidades estables). */
const seedFrom = (key: string): number => {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return h;
};

// ─────────────────────────────── Imágenes ──────────────────────────────────
/**
 * Pool de fotos curadas de Unsplash por categoría.
 *
 * `source.unsplash.com` fue deprecado en 2024 y `loremflickr.com` devuelve
 * imágenes default (un gato) cuando el keyword no coincide con su índice.
 * Para garantizar imágenes COHERENTES con cada producto usamos URLs directas
 * a `images.unsplash.com` con IDs de fotos públicas reales, agrupadas por
 * categoría. No requieren API key y tienen uso libre (licencia Unsplash).
 *
 * Para añadir más imágenes:
 *   1. Entra a https://unsplash.com/s/photos/<keyword>
 *   2. Abre la foto → copia el segmento `photo-XXXXX-YYYYYY` de la URL
 *   3. Añádelo al array correspondiente de CATEGORY_IMAGE_POOL
 */
const CATEGORY_IMAGE_POOL: Record<string, string[]> = {
  // ── Alimentos / Insumos ──
  cat_gra: [
    "1586201375761-83865001e31c", // arroz
    "1505253716362-afaea1d3d1af", // granos
    "1627147105017-def5deca92c8", // pasta espaguetis
    "1509440159596-0249088772ff", // harina
    "1586444248902-2f64eddc13df", // legumbres
  ],
  cat_lac: [
    "1550583724-b2692b85b150", // leche
    "1559598467-f8b76c8155d0", // queso
    "1488477181946-6428a0291777", // yogurt
    "1589985270826-4b7bb135bc9d", // mantequilla
    "1628088062854-d1870b4553da", // lacteos
  ],
  cat_fv: [
    "1542838132-92c53300491e", // verduras
    "1610832958506-aa56368176cf", // frutas
    "1557844352-761f2565b576", // manzanas
    "1518843875459-f738682238a6", // tomates
    "1519996529931-28324d5a630e", // frutas mixtas
  ],
  cat_salsa: [
    "1472476443507-c7a5948772fc", // salsas
    "1607098665874-fd193397547b", // ketchup
    "1585637071663-799845ad5212", // condimentos
    "1472476443507-c7a5948772fc", // aceite oliva
  ],
  cat_pan: [
    "1509440159596-0249088772ff", // pan
    "1555507036-ab1f4038808a", // baguette
    "1598373182133-52452f7691ef", // pasteleria
    "1608198093002-ad4e005484ec", // pan artesano
  ],
  cat_beb: [
    "1556881286-fc6915169721", // botellas
    "1622483767028-3f66f32aef97", // agua
    "1554866585-cd94860890b7", // jugos
    "1544145945-f90425340c7e", // cerveza
    "1497534446932-c925b458314e", // café
    "1563636619-e9143da7973b", // bebidas leche botella
  ],
  cat_car: [
    "1588168333986-5078d3ae3976", // carne
    "1603048588665-791ca8aea617", // filete
    "1607623814075-e51df1bdc82f", // pollo
    "1529692236671-f1f6cf9683ba", // cerdo
  ],
  cat_pesce: [
    "1580554530778-ca36943938b2", // pescado
    "1565680018434-b513d5e5fd47", // salmon
    "1559742811-822873691df8", // mariscos
    "1599599810769-bcde5a160d32", // camarones
  ],

  // ── Comida preparada (recetas) ──
  cat_fast: [
    "1568901346375-23c9450c58cd", // hamburguesa
    "1571091718767-18b5b1457add", // burger
    "1586190848861-99aa4a171e90", // hot dog
    "1513104890138-7c749659a591", // pizza
    "1565299624946-b28f40a0ae38", // pizza variada
  ],
  cat_rest: [
    "1504674900247-0877df9cc836", // plato
    "1555939594-58d7cb561ad1", // pasta gourmet
    "1546069901-ba9599a7e63c", // bowl
    "1565299507177-b0ac66763828", // paella
  ],
  cat_desay: [
    "1547592166-23ac45744acd", // caldo / sopa
    "1546069901-ba9599a7e63c", // bowl
    "1504674900247-0877df9cc836", // plato
    "1580554530778-ca36943938b2", // pescado
    "1555939594-58d7cb561ad1", // plato completo
    "1497534446932-c925b458314e", // café
    "1509440159596-0249088772ff", // pan
    "1551218808-94e220e084d2", // desayuno
    "1607623814075-e51df1bdc82f", // pollo / carne
    "1550583724-b2692b85b150", // leche / chocolate
    "1598373182133-52452f7691ef", // panadería
    "1565299507177-b0ac66763828", // plato abundante
    "1568901346375-23c9450c58cd", // comida rápida
  ],
  cat_extra: [
    "1497534446932-c925b458314e", // café
    "1550583724-b2692b85b150", // chocolate
    "1509440159596-0249088772ff", // pan
    "1610836937051-545514635e1b", // fruta
    "1547592166-23ac45744acd", // sopa
    "1607623814075-e51df1bdc82f", // proteína
    "1555939594-58d7cb561ad1", // plato / extras
  ],
  cat_hela: [
    "1501443762994-82bd5dace89a", // helado
    "1563805042-7684c019e1cb", // cono
    "1588964895597-cfccd6e2dbf9", // postre
    "1488477181946-6428a0291777", // crema
  ],

  // ── Salud / Aseo / Mascotas ──
  cat_farm: [
    "1587854692152-cbe660dbde88", // medicamentos
    "1584308666744-24d5c474f2ae", // farmacia
    "1576091160399-112ba8d25d1d", // pastillas
    "1471864190281-a93a3070b6de", // termometro
  ],
  cat_aseo: [
    "1585421514738-01798e348b17", // limpieza
    "1563453392212-326f5e854473", // jabon
    "1610557892470-55d9e80c0bce", // detergente
    "1583947581924-860bda6a26df", // aseo hogar
  ],
  cat_vet: [
    "1583337130417-3346a1be7dee", // veterinaria
    "1601758228041-f3b2795255f1", // perro
    "1574158622682-e40e69881006", // gato vet
    "1587300003388-59208cc962cb", // pet care
  ],
  cat_masc: [
    "1587300003388-59208cc962cb", // mascotas
    "1601758003122-53c40e686a19", // perro feliz
    "1535268647677-300dbf3d78d1", // gato
    "1415369629372-26f2fe60c467", // comida perro
  ],

  // ── Moda / Accesorios ──
  cat_ropa: [
    "1521572163474-6864f9cf17ab", // camiseta
    "1542272604-787c3835535d", // jean
    "1525507119028-ed4c629a60a3", // blusa
    "1551698618-1dfe5d97d256", // vestido
    "1556821840-3a63f95609a7", // hoodie
  ],
  cat_calz: [
    "1542291026-7eec264c27ff", // tenis
    "1600269452121-4f2416e55c28", // sneakers
    "1595950653106-6c9ebd614d3a", // botas
    "1560769629-975ec94e6a86", // zapatos
  ],
  cat_acc: [
    "1548036328-c9fa89d128fa", // bolso
    "1548036328-c9fa89d128fa", // billetera (usando imagen de bolso - similar)
    "1577803645773-f96470509666", // reloj
    "1511499767150-a48a237f0083", // gafas
  ],
  cat_joy: [
    "1515562141207-7a88fb7ce338", // anillo
    "1599643478518-a784e5dc4c8f", // cadena
    "1535632066927-ab7c9ab60908", // aretes
    "1617038220319-276d3cfab638", // joyeria
  ],
  cat_bell: [
    "1596462502278-27bfdc403348", // labial
    "1596462502278-27bfdc403348", // maquillaje (usando imagen de labial - similar)
    "1571781926291-c477ebfd024b", // perfume
    "1556228720-195a672e8a03", // cremas
  ],

  // ── Hogar / Técnica ──
  cat_hogar: [
    "1586023492125-27b2c045efd7", // sabanas
    "1556909114-f6e7ad7d3136", // vajilla
    "1565193566173-7a0ee3dbe261", // olla
    "1522771739844-6a9f6d5f14af", // hogar
  ],
  cat_mue: [
    "1555041469-a586c61ea9bc", // sofa
    "1493663284031-b7e3aefcae8e", // mesa
    "1506439773649-6e0eb8cfb237", // silla
    "1505693416388-ac5ce068fe85", // cama
  ],
  cat_elec: [
    "1585771724684-38269d6639fd", // nevera
    "1626806787461-102c1bfaaea1", // lavadora
    "1574269909862-7e1d70bb8078", // estufa
    "1567016432779-094069958ea5", // microondas
  ],
  cat_tech: [
    "1592750475338-74b7b21085ab", // iphone
    "1511707171634-5f897ff02aa9", // smartphone
    "1588872657578-7efd1f1555ed", // laptop
    "1593642632823-8f785ba67e45", // tech
    "1505740420928-5e560c06d30e", // headphones
  ],
  cat_ferr: [
    "1581783898377-1c85bf937427", // taladro
    "1530124566582-a618bc2615dc", // herramientas
    "1504148455328-c376907d081c", // ferreteria
    "1572981779307-38b8cabb2407", // martillo
  ],
  cat_auto: [
    "1553440569-bcc63803a83d", // auto
    "1580273916550-e323be2ae537", // llantas
    "1493238792000-8113da705763", // motor
    "1580273916550-e323be2ae537", // accesorios carro
  ],

  // ── Especialidad ──
  cat_jug: [
    "1566576912321-d58ddd7a6088", // juguetes
    "1558877385-81a1c7e67d72", // lego
    "1596461404969-9ae70f2830c1", // barbie
    "1545558014-8692077e9b5c", // carros juguete
  ],
  cat_pap: [
    "1519241047957-be31d7379a5d", // papeleria
    "1455390582262-044cdead277a", // cuaderno
    "1456735190827-d1262f71b8a3", // oficina
    "1568667256549-094345857637", // utiles
  ],
  cat_dep: [
    "1534438327276-14e5300c3a48", // gym
    "1571019613454-1cb2f99b2d8b", // pesas
    "1599058917765-a780eda07a3e", // deporte
    "1517836357463-d25dfeac3438", // pelota
  ],
  cat_flor: [
    "1490750967868-88aa4486c946", // rosas
    "1526047932273-341f2a7631f9", // bouquet
    "1563241527-3004b7be0ffd", // flores
    "1487530811176-3780de880c2d", // ramo
  ],
  cat_serv: [
    "1521590832167-7bcbfaa6381f", // spa
    "1562322140-8baeececf3df", // servicio
    "1560066984-138dadb4c035", // corte cabello
    "1540555700478-4be289fbecef", // manicure
  ],
  cat_mini: [
    "1601599561213-832382fd07ba", // minimercado
    "1542838132-92c53300491e", // abarrotes
    "1604719312566-8912e9227c6a", // tienda
    "1604719312566-8912e9227c6a", // productos
  ],
  cat_lib: [
    "1544947950-fa07a98d237f", // libros
    "1512820790803-83ca734da794", // libro abierto
    "1519682337058-a94d519337bc", // libreria
    "1495446815901-a7297e633e8d", // biblioteca
  ],
  cat_snk: [
    "1621996346565-e3dbc646d9a9", // papas fritas
    "1566478989037-eec170784d0b", // snacks
    "1625220194771-7ebdea0b70b9", // chocolates
    "1582716401301-b2407dc7563d", // dulces chocolates
  ],
};

/** Selecciona una foto del pool de manera determinista por seed. */
const pickFromPool = (pool: string[], seedStr: string, offset = 0): string => {
  let h = 0;
  for (let i = 0; i < seedStr.length; i++)
    h = (h * 31 + seedStr.charCodeAt(i)) >>> 0;
  const idx = (h + offset) % pool.length;
  return pool[idx];
};

/** URL final de Unsplash a partir de un photo-id. */
const buildUnsplashUrl = (photoId: string): string =>
  `https://images.unsplash.com/photo-${photoId}?w=400&h=400&fit=crop&auto=format&q=70`;

/**
 * Devuelve una URL de imagen del pool curado para la categoría dada.
 * Usado por `_variants.ts` para generar swatches de color con imágenes coherentes.
 * Si la categoría no está en el pool, retorna null.
 */
export const pickCategoryImage = (
  categoryId: string,
  seed: string | number,
  offset = 0,
): string | null => {
  const pool = CATEGORY_IMAGE_POOL[categoryId];
  if (!pool || pool.length === 0) return null;
  return buildUnsplashUrl(pickFromPool(pool, String(seed), offset));
};

/**
 * Genera una URL de imagen coherente con la categoría (legacy helper).
 * Mantenido por compat con otros módulos que importen `img`.
 */
export const img = (keywords: string[], seed?: string | number): string => {
  // Fallback genérico cuando se llama sin categoría: usa picsum seeded.
  const s = String(seed ?? keywords.join(","));
  return `https://picsum.photos/seed/${encodeURIComponent(s)}/400/400`;
};

/**
 * Genera 3 URLs de imagen para la galería del producto.
 * Recibe la categoría para escoger fotos del pool curado de Unsplash;
 * si la categoría no tiene pool (edge case), usa picsum seeded.
 */
export const img3 = (
  _keywords: string[],
  baseSeed: string | number,
  categoryId?: string,
): string[] => {
  const pool = categoryId ? CATEGORY_IMAGE_POOL[categoryId] : undefined;
  const seedStr = String(baseSeed);

  if (pool && pool.length > 0) {
    return [
      buildUnsplashUrl(pickFromPool(pool, seedStr, 0)),
      buildUnsplashUrl(pickFromPool(pool, seedStr, 1)),
      buildUnsplashUrl(pickFromPool(pool, seedStr, 2)),
    ];
  }

  // Fallback (no debería ocurrir si la categoría está en el pool)
  return [
    `https://picsum.photos/seed/${encodeURIComponent(seedStr)}-1/400/400`,
    `https://picsum.photos/seed/${encodeURIComponent(seedStr)}-2/400/400`,
    `https://picsum.photos/seed/${encodeURIComponent(seedStr)}-3/400/400`,
  ];
};

// ─────────────────────────── Catálogo de gastos ────────────────────────────
/** Gastos operativos estándar activos por defecto (13% total aprox). */
const DEFAULT_GASTO_FACTORS: Record<string, { enabled: boolean; pct: number }> =
  {
    arriendo: { enabled: true, pct: 1 },
    empleados: { enabled: true, pct: 2 },
    servicios: { enabled: true, pct: 1 },
    iva: { enabled: true, pct: 3 },
    mermas: { enabled: true, pct: 1 },
    transporte: { enabled: true, pct: 1 },
    manoObra: { enabled: true, pct: 1 },
    empaque: { enabled: true, pct: 1 },
    comisiones: { enabled: true, pct: 1 },
    otros: { enabled: true, pct: 1 },
  };

// ───────────────────────── Tipos de entrada ────────────────────────────────
export interface RecipeIngredient {
  nombre: string;
  cantidadCompra: number;
  unidadCompra: string;
  costoCompra: number;
  cantidadUso: number;
  unidadUso: string;
}

export interface ProductInput {
  // Identificación
  categoryId: string; // ej: "cat_beb"
  skuPrefix: string; // ej: "BEB"
  index: number; // posición en la categoría (0..9)
  categoria: string; // ej: "Bebidas"
  nombre: string;

  // Marca / Identidad
  marca: string;
  modelo?: string;
  origen?: string;
  condicion?: "nuevo" | "usado" | "reacondicionado";

  // Precios base
  costo: number; // costo unitario de compra
  precio: number; // precio cliente al público

  // Inventario
  stock?: number; // default 50
  stockMinimo?: number; // default 10
  stockMaximo?: number; // default 120
  unidad?: string; // default "unidad"
  cantidadLote?: number; // default 1
  ubicacionFisica: string;
  proveedor: string;
  tiempoEntrega?: string; // default "2-3 días"
  codigoBarras?: string; // default generado
  vencimientoMeses?: number; // si aplica
  ventaPorPeso?: boolean;
  /** Si `ventaPorPeso`: precio por kg/L; por defecto se usa `precio` del ítem. */
  precioPorKilo?: number;
  /** Unidad de venta al peso (kg, lb, g). Por defecto `unidad` o "kg". */
  unidadPeso?: string;
  /** Código de báscula (PLU) que programa el carnicero en su báscula. */
  plu?: number;

  // Tienda (marketing)
  descripcionLarga: string;
  ingredientes?: string;
  especificaciones?: { clave: string; valor: string }[];
  material?: string;
  colorProducto?: string;
  talla?: string;
  dimensiones?: string;
  pesoNeto?: string;
  garantia?: string;

  // Impuestos
  tieneIVA?: boolean;
  porcentajeIVA?: number;

  // Visual (3 imágenes obligatorias)
  imageKeywords: string[]; // ej: ["arroz","grano","blanco"]
  /** Galería fija; si no se pasa, se genera desde el pool de la categoría. */
  galeria?: string[];
  color?: string; // tailwind ej: "bg-amber-500"
  shape?: "circle" | "square" | "hexagon" | "diamond";

  // Receta (producto combinado)
  esReceta?: boolean;
  recetaRendimiento?: number;
  recetaIngredientes?: RecipeIngredient[];

  // Avanzado
  estado?: "activo" | "pausado" | "descontinuado";
  destacado?: boolean;
  gastoMargen?: "conservador" | "estandar" | "ambicioso";
  gastoRedondeo?: boolean;

  // Variantes (opcional; si no se pasa, se infiere por categoría si aplica)
  variantes?: VariantGroup[];
  variantesSKUs?: VariantSKU[];
  /** Modificadores restaurante (extras, bebida, proteína…). */
  gruposModificadores?: ModifierGroup[];
  /** Validación en caja: libre = venta rápida sin completar todos los grupos. */
  modificadoresValidacionCaja?: "completa" | "libre";
  /** Disponible en tienda virtual; default true. */
  isForSale?: boolean;
  /** Franja horaria de venta en tienda/caja. */
  disponibilidadHoraria?: DisponibilidadHoraria;
}

// ─────────────────────────── Build del producto ────────────────────────────
export const buildProduct = (i: ProductInput): Producto => {
  const id = `${i.categoryId}_${i.index}`;
  const codigo = `${i.skuPrefix}-${String(i.index + 1).padStart(3, "0")}`;
  const codigoBarras =
    i.codigoBarras ?? `770${String(seedFrom(id) % 1e10).padStart(10, "0")}`;

  // Variantes: si el caller pasó explícitas, priorizar; si no, intentar inferir por categoría.
  const bundle =
    i.variantes || i.variantesSKUs
      ? { variantes: i.variantes ?? [], variantesSKUs: i.variantesSKUs ?? [] }
      : inferVariantesPorCategoria(i.categoryId, id);

  // Costo real a partir de receta (si aplica)
  const costoFinal =
    i.esReceta && i.recetaIngredientes?.length
      ? i.recetaIngredientes.reduce((s, ing) => {
          const perUnit =
            ing.costoCompra * (ing.cantidadUso / ing.cantidadCompra);
          return s + perUnit;
        }, 0) / (i.recetaRendimiento ?? 1)
      : i.costo;

  // Precios derivados
  const precio = smartRound(i.precio);
  const precioMayorista = smartRound(precio * 0.85);
  const precioProveedor = smartRound(costoFinal * 0.95);

  // Stock sensato
  const stock = i.stock ?? 50;
  const stockMinimo = i.stockMinimo ?? Math.max(5, Math.round(stock * 0.15));
  const stockMaximo = i.stockMaximo ?? Math.round(stock * 2);

  // Fechas estables por id
  const s = seedFrom(id);
  const llegadaAgo = (s % 10) + 1; // 1..10 días atrás
  const createdAgo = 30 + (s % 90); // 30..119 días atrás
  const updatedAgo = s % 5; // 0..4 días atrás

  // Vencimiento
  const tieneVencimiento = !!i.vencimientoMeses;
  const fechaVencimiento = tieneVencimiento
    ? daysAgo(-30 * (i.vencimientoMeses as number))
    : undefined;

  // Velocidad de venta aproximada
  const velocidad = Math.max(1, Math.round(stock / ((s % 12) + 4)));

  return {
    id,
    businessId: "business_demo",
    nombre: i.nombre,
    descripcion: i.descripcionLarga.slice(0, 120),
    descripcionLarga: i.descripcionLarga,
    marca: i.marca,
    modelo: i.modelo,
    origen: i.origen ?? "Colombia",
    condicion: i.condicion ?? "nuevo",
    ingredientes: i.ingredientes,
    especificaciones: i.especificaciones ?? [],
    material: i.material,
    colorProducto: i.colorProducto,
    talla: i.talla,
    garantia: i.garantia,
    dimensiones: i.dimensiones,
    pesoNeto: i.pesoNeto,

    categoria: i.categoria,
    categoriaId: i.categoryId,

    precio,
    costo: smartRound(costoFinal),
    stock,
    stockMinimo,
    stockMaximo,
    ubicacionFisica: i.ubicacionFisica,
    unidad: i.unidad ?? "unidad",
    codigo,
    codigoBarras,

    precioMayorista,
    cantidadMinimaMayorista: 5,
    // El catálogo demo participa del descuento VIP por nivel del programa de
    // fidelización (antes: "precio VIP" plano = 92%).
    aceptaDescuentoVIP: true,
    descuentoMaximo: 15,

    tieneIVA: i.tieneIVA ?? false,
    porcentajeIVA: i.porcentajeIVA,

    tieneVencimiento,
    fechaVencimiento,
    alertaVencimiento30: tieneVencimiento,
    alertaVencimiento15: tieneVencimiento,
    alertaVencimiento7: tieneVencimiento,

    proveedor: i.proveedor,
    precioProveedor,
    tiempoEntrega: i.tiempoEntrega ?? "2-3 días",

    estado: i.estado ?? "activo",
    destacado: !!i.destacado,

    visual: {
      color: i.color ?? "bg-slate-500",
      shape: i.shape ?? "square",
      hasBorder: false,
    },

    galeria: i.galeria ?? img3(i.imageKeywords, id, i.categoryId),

    variantes: bundle?.variantes,
    variantesSKUs: bundle?.variantesSKUs,
    gruposModificadores: i.gruposModificadores,
    ...(i.modificadoresValidacionCaja
      ? { modificadoresValidacionCaja: i.modificadoresValidacionCaja }
      : {}),

    ventaPorPeso: !!i.ventaPorPeso,
    ...(i.ventaPorPeso
      ? {
          precioPorKilo:
            i.precioPorKilo != null ? smartRound(i.precioPorKilo) : precio,
          unidadPeso: (i.unidadPeso ?? i.unidad ?? "kg").toLowerCase(),
          ...(i.plu != null ? { plu: String(i.plu) } : {}),
        }
      : {}),
    isForSale: i.isForSale ?? true,
    ...(i.disponibilidadHoraria
      ? { disponibilidadHoraria: i.disponibilidadHoraria }
      : {}),
    isActive: true,

    esReceta: !!i.esReceta,
    recetaRendimiento: i.recetaRendimiento,
    recetaIngredientes: i.recetaIngredientes,
    cantidadLote: i.cantidadLote ?? 1,

    fechaLlegada: daysAgo(llegadaAgo),
    velocidadVenta: velocidad,
    stockAlReponer: stockMaximo,
    ultimaDuracionDias: Math.round(stockMaximo / velocidad),
    historialDuraciones: [
      Math.round(stockMaximo / velocidad) - 1,
      Math.round(stockMaximo / velocidad),
      Math.round(stockMaximo / velocidad) + 1,
    ],

    gastoFactors: { ...DEFAULT_GASTO_FACTORS },
    gastoMargen: i.gastoMargen ?? "estandar",
    gastoRedondeo: i.gastoRedondeo ?? true,

    createdAt: daysAgo(createdAgo),
    updatedAt: daysAgo(updatedAgo),
  };
};
